import { supabase } from '../../../src/lib/supabase';

/** Mesmo contrato usado por DocumentUploadModal.tsx: reenvia titulo/pasta/documentos_json atuais + o(s) novo(s) arquivo(s). */
export async function uploadDocumentToNegotiation(
    negotiationId: string | number,
    file: File,
    customName: string
): Promise<{ ok: boolean; error?: string }> {
    const [{ data: settings }, { data: negotiationData }] = await Promise.all([
        supabase.from('system_settings').select('value').eq('key', 'documents_upload_webhook_url').single(),
        supabase.from('negociacoes').select('titulo, pasta_sharepoint_url, documentos_json').eq('id', negotiationId).single(),
    ]);

    if (!settings?.value) {
        return { ok: false, error: 'URL de envio de documentos não configurada. Configure em Configurações > APIs.' };
    }

    const formData = new FormData();
    formData.append('id', String(negotiationId));
    formData.append('titulo', negotiationData?.titulo || '');
    formData.append('pasta_sharepoint_url', negotiationData?.pasta_sharepoint_url || '');
    formData.append('documentos_json', JSON.stringify(negotiationData?.documentos_json || []));
    formData.append('files', file);
    formData.append('file_names', customName);

    try {
        const response = await fetch(settings.value, { method: 'POST', body: formData });
        if (!response.ok) {
            return { ok: false, error: `Falha ao anexar o PDF da fatura aos documentos (erro ${response.status}).` };
        }
        return { ok: true };
    } catch (error: any) {
        return { ok: false, error: error.message || 'Erro de conexão ao anexar o PDF da fatura aos documentos.' };
    }
}

/**
 * Anexa vários arquivos em uma ÚNICA chamada. Importante: uploadDocumentToNegotiation faz um
 * read-modify-write do array documentos_json inteiro a cada chamada — disparar várias em sequência
 * (uma por fatura + uma pelo documento) é uma condição de corrida: quem terminar de gravar por último
 * no n8n sobrescreve o array com a sua própria leitura desatualizada, apagando os anexos das chamadas
 * anteriores. Batching em uma única requisição evita isso.
 */
export async function uploadDocumentsToNegotiation(
    negotiationId: string | number,
    files: { file: File; customName: string }[]
): Promise<{ ok: boolean; error?: string }> {
    if (files.length === 0) return { ok: true };

    const { data: settings } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'documents_upload_webhook_url')
        .single();

    if (!settings?.value) {
        return { ok: false, error: 'URL de envio de documentos não configurada. Configure em Configurações > APIs.' };
    }

    const { data: negotiationData } = await supabase
        .from('negociacoes')
        .select('titulo, pasta_sharepoint_url, documentos_json')
        .eq('id', negotiationId)
        .single();

    const formData = new FormData();
    formData.append('id', String(negotiationId));
    formData.append('titulo', negotiationData?.titulo || '');
    formData.append('pasta_sharepoint_url', negotiationData?.pasta_sharepoint_url || '');
    formData.append('documentos_json', JSON.stringify(negotiationData?.documentos_json || []));
    files.forEach(({ file, customName }) => {
        formData.append('files', file);
        formData.append('file_names', customName);
    });

    try {
        const response = await fetch(settings.value, { method: 'POST', body: formData });
        if (!response.ok) {
            return { ok: false, error: `Falha ao anexar os arquivos aos documentos (erro ${response.status}).` };
        }
        return { ok: true };
    } catch (error: any) {
        return { ok: false, error: error.message || 'Erro de conexão ao anexar os arquivos aos documentos.' };
    }
}
